import { Box, Button, Grid } from "@mui/material";
import { format } from "date-fns";

import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import removeIcon from "../../assets/icons/delete.png";
import { useAuth } from "../../features/auth/AuthContext";
import { useAddCargoTemplateMutation } from "../../features/cargo/cargoApiSlice";
import { AddAsTemplateButton } from "../AddAsTemplateButton";
import { CopyCargoButton } from "../CopyCargoButton";

export const CargosList = ({
	settings,
	cargos,
	rowErrors,
	handleRemoveRow,
	handleCopyCargo,
}) => {
	const { t } = useTranslation();
	const { user } = useAuth();
	const hasCustomTransConnection = user?.hasCustomTransConnection;

	const { enqueueSnackbar } = useSnackbar();
	const [saveAsTemplateMutation, { isLoading: isSavingTemplate }] =
		useAddCargoTemplateMutation();

	const handleSaveAsTemplate = (template) => {
		if (isSavingTemplate) return;

		const data = {
			name: t("template_name"),
			loadingAddressCountry: template.loadingAddressCountry,
			...(template.loadingAddress && {
				loadingAddress: template.loadingAddress.id,
			}),
			...(!template.loadingAddress && {
				loadingAddressCustomCountry: template.loadingAddressCustomCountry,
				loadingAddressCustomCity: template.loadingAddressCustomCity,
				loadingAddressCustomPostalCode: template.loadingAddressCustomPostalCode,
			}),
			unloadingAddressCountry: template.unloadingAddressCountry,
			...(template.unloadingAddress && {
				unloadingAddress: template.unloadingAddress.id,
			}),
			...(!template.unloadingAddress && {
				unloadingAddressCustomCountry: template.unloadingAddressCustomCountry,
				unloadingAddressCustomCity: template.unloadingAddressCustomCity,
				unloadingAddressCustomPostalCode:
					template.unloadingAddressCustomPostalCode,
			}),
			weight: template.weight,
			length: template.length,
			vehicleTypeId: template.vehicleTypeId,
			vehicleBodyId: template.vehicleBodyId,
			additionalRequirements: template.additionalRequirements || [],
			cargoTypeId: hasCustomTransConnection ? template?.cargoTypeId || "" : "",
		};

		saveAsTemplateMutation({ data })
			.unwrap()
			.then(() => {
				enqueueSnackbar(t("template_saved"), {
					variant: "success",
					preventDuplicate: true,
				});
			})
			.catch((error) => {
				if (error?.data?.errors) {
					const errors = error.data.errors;
					Object.entries(errors).forEach(([_key, message]) => {
						enqueueSnackbar(message[0], {
							variant: "error",
							preventDuplicate: true,
						});
					});
				} else {
					enqueueSnackbar(error?.data?.message || t("default_error"), {
						variant: "error",
						preventDuplicate: true,
					});
				}
			});
	};

	return (
		<Grid size={12}>
			<Box
				className="cargos-list"
				component="table"
				sx={{
					width: "100%",
					borderCollapse: "separate",
					borderSpacing: "0 3px",
				}}
			>
				{cargos.map((field, index) => {
					return (
						<Box
							key={`cargo-${field.id}`}
							component="tr"
							sx={{
								width: "100%",
								background: rowErrors.includes(index) ? "#d32f2f1d" : "#F5F5F5",
							}}
						>
							<td>
								{field?.loadingDateType === "day" &&
									format(new Date(field.loadingDate), "dd.MM")}
								{field?.loadingDateType === "range" &&
									field?.loadingDateRange
										.map((date) => format(new Date(date), "dd.MM"))
										.join(" - ")}
							</td>
							{field?.loadingAddress ? (
								<td>
									{field?.loadingAddress.address.countryCode}{" "}
									{field?.loadingAddress.address.postalCode}{" "}
									{field?.loadingAddress.address.city}
								</td>
							) : (
								<td>
									{field?.loadingAddressCustomCountry}{" "}
									{field?.loadingAddressCustomPostalCode}{" "}
									{field?.loadingAddressCustomCity}
								</td>
							)}
							<td>
								{field?.unloadingDateType === "day" &&
									format(new Date(field.unloadingDate), "dd.MM")}
								{field?.unloadingDateType === "range" &&
									field?.unloadingDateRange
										.map((date) => format(new Date(date), "dd.MM"))
										.join(" - ")}
							</td>
							{field?.unloadingAddress ? (
								<td>
									{field.unloadingAddress.address.countryCode}{" "}
									{field.unloadingAddress.address.postalCode}{" "}
									{field.unloadingAddress.address.city}
								</td>
							) : (
								<td>
									{field?.unloadingAddressCustomCountry}{" "}
									{field?.unloadingAddressCustomPostalCode}{" "}
									{field?.unloadingAddressCustomCity}
								</td>
							)}
							<td>{field?.weight}</td>
							<td>{field?.length}</td>
							<td>
								{field?.vehicleTypeId
									.map((id) => {
										return settings.vehicleOptions.vehicleTypesOptions[id].name;
									})
									.join(", ")}
							</td>
							<td>
								{field?.vehicleBodyId
									.map((id) => {
										return settings.vehicleOptions.vehicleBodyTypesOptions[id];
									})
									.join(", ")}
							</td>
							<td style={{ width: "1%" }}>
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										gap: "4px",
									}}
								>
									<CopyCargoButton
										handleCopyCargo={() => {
											const cargo = field;
											// remove "cargos" from cargo
											delete cargo.cargos;
											handleCopyCargo(field);
										}}
									/>
									<AddAsTemplateButton
										handleAddAsTemplate={() => handleSaveAsTemplate(field)}
									/>
									<Button
										color="error"
										variant="text"
										onClick={() => handleRemoveRow(index)}
										sx={{
											width: "auto",
											minWidth: "unset",
											background: "transparent",
											"&:hover": {
												background: "transparent",
											},
											py: 0,
											px: "3px",
										}}
									>
										<Box
											sx={{
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												cursor: "pointer",
												height: "16px",
												width: "16px",
											}}
										>
											<img
												src={removeIcon}
												alt=""
												style={{
													width: "100%",
													height: "100%",
													objectFit: "contain",
												}}
											/>
										</Box>
									</Button>
								</Box>
							</td>
						</Box>
					);
				})}
			</Box>
		</Grid>
	);
};
